import { useQueryClient } from '@gogame/query';
import { useScrollToDown, useTypingAnimation } from 'components/AIChatbot';
import { messages } from './queryKeyFactory';

/**
 * useOptimisticUpdateMessages
 *
 * This hook provides an updater function for optimistically updating chat messages in the react-query cache.
 * When a bot message with a jobId is added, it triggers a typewriter animation using useTypingAnimation.
 * For user messages, it simply appends the message to the cache.
 *
 * - If a message with the same jobId already exists, it updates it and restarts the animation.
 * - When adding a new bot message, it also scrolls the chat to the bottom after a short delay.
 *
 * @returns {(chatId: string|number, message: object, jobId?: string|number) => void}
 *   Function to optimistically update messages for a given chat.
 */
export const useOptimisticUpdateMessages = () => {
  const queryClient = useQueryClient();
  const startTypingAnimation = useTypingAnimation();
  const scrollToDown = useScrollToDown();
  return (chatId, message, jobId = null) => {
    queryClient.setQueriesData(
      {
        queryKey: messages(chatId),
        exact: false,
      },
      (old = []) => {
        if (!Array.isArray(old)) old = [];
        if (jobId) {
          const found = old.some((el) => el.jobId === jobId);
          if (found) {
            let result = old.map((el) => {
              return el.jobId === jobId
                ? {
                    ...message,
                    originalText: message.text,
                    typingIterator: el.typingIterator || 0,
                    text: el.text,
                    jobId,
                  }
                : el;
            });

            return result;
          } else {
            // Scroll to bottom after adding a new bot message
            setTimeout(() => {
              scrollToDown('[data-cy="View"] > [data-cy="Box"]');
            }, 100);

            return [
              ...old,
              {
                ...message,
                originalText: message.text,
                text: '',
                typingIterator: 0,
                jobId,
              },
            ];
          }
        }
        return [...old, message]; // user message
      }
    );

    startTypingAnimation({ queryKey: messages(chatId), jobId });
  };
};
